
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function Services() {
  const headingAnim = useScrollAnimation({ threshold: 0.2 });
  const cardsAnim = useStaggerAnimation(3, { threshold: 0.1 });

  const services = [
    {
      icon: 'ri-layout-line',
      title: 'Website Design & Development',
      description: 'Custom, mobile-first websites built to convert visitors into customers.',
      benefits: [
        'Custom, mobile-first design',
        'Fast loading & optimized',
        'Easy to update content',
      ],
      color: 'bg-amber-50',
      iconColor: 'text-amber-700',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local SEO Foundation',
      description: 'Get found by nearby customers searching for your services on Google.',
      benefits: [
        'Google Business Profile setup',
        'Local keyword optimization',
        'Citation building & listings',
      ],
      color: 'bg-emerald-50',
      iconColor: 'text-emerald-700',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Maintenance & Updates',
      description: 'Keep your site secure, fast, and up-to-date without lifting a finger.',
      benefits: [
        'Security & hosting included',
        'Regular content updates',
        'Performance monitoring',
      ],
      color: 'bg-rose-50',
      iconColor: 'text-rose-700',
    },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headingAnim.ref}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-16"
          style={{
            opacity: headingAnim.isVisible ? 1 : 0,
            transform: headingAnim.isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div>
            <span className="font-inter font-semibold text-xs tracking-widest uppercase text-primary/60 mb-3 block">What We Do</span>
            <h2 className="font-sora font-bold text-4xl lg:text-[44px] text-charcoal leading-tight">
              Everything you need to<br />grow online
            </h2>
          </div>
          <Link
            to="/services"
            className="font-inter font-medium text-sm text-primary hover:text-primary/70 transition-colors inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
          >
            View all services
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div ref={cardsAnim.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              style={cardsAnim.getItemStyle(index)}
            >
              <div className={`w-14 h-14 rounded-2xl ${service.color} flex items-center justify-center mb-6`}>
                <i className={`${service.icon} text-2xl ${service.iconColor}`}></i>
              </div>
              
              <h3 className="font-sora font-semibold text-lg text-charcoal mb-2">
                {service.title}
              </h3>

              <p className="font-inter text-sm text-gray-500 mb-5 leading-relaxed">
                {service.description}
              </p>

              <ul className="space-y-2.5">
                {service.benefits.map((benefit, idx) => (
                  <li
                    key={idx}
                    className="font-inter text-sm text-gray-600 flex items-center gap-2"
                  >
                    <i className="ri-check-line text-emerald-500 text-base"></i>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
